import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { useSiteContent, saveSiteContent } from "@/lib/siteContent";

const Text = ({ label, value, onChange, testid, area = false, rows = 2 }) => (
  <div>
    <Label className="text-slate-300 text-xs">{label}</Label>
    {area
      ? <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="bg-[#07090E] border-slate-800 mt-1" data-testid={testid} />
      : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-[#07090E] border-slate-800 mt-1 h-9" data-testid={testid} />}
  </div>
);

export default function SiteContentEditor() {
  const { content, reload } = useSiteContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (content && !draft) setDraft(JSON.parse(JSON.stringify(content))); }, [content]);

  if (!draft) return <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin"/>Carregando conteúdo…</div>;

  const setH = (k, v) => setDraft((d) => ({ ...d, home: { ...d.home, [k]: v } }));
  const setP = (k, v) => setDraft((d) => ({ ...d, produtos_page: { ...d.produtos_page, [k]: v } }));
  const setC = (k, v) => setDraft((d) => ({ ...d, contato: { ...d.contato, [k]: v } }));
  const setListItem = (path, idx, k, v) => setDraft((d) => {
    const home = { ...d.home }; const arr = [...(home[path] || [])];
    arr[idx] = { ...arr[idx], [k]: v }; home[path] = arr;
    return { ...d, home };
  });
  const addItem = (path, tpl) => setDraft((d) => ({ ...d, home: { ...d.home, [path]: [...(d.home[path] || []), tpl] } }));
  const rmItem = (path, idx) => setDraft((d) => {
    const arr = [...(d.home[path] || [])]; arr.splice(idx, 1);
    return { ...d, home: { ...d.home, [path]: arr } };
  });

  const save = async () => {
    setSaving(true);
    try { await saveSiteContent(draft); toast.success("Conteúdo do site salvo"); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const h = draft.home || {}; const pp = draft.produtos_page || {}; const cc = draft.contato || {};

  return (
    <div className="space-y-6">
      {/* HOME - HERO */}
      <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 space-y-3">
        <div className="font-medium text-sm text-slate-300">Home — Hero</div>
        <Text label="Kicker (badge)" value={h.hero_kicker} onChange={(v)=>setH("hero_kicker",v)} testid="cms-hero-kicker"/>
        <div className="grid grid-cols-2 gap-3">
          <Text label="Título — linha 1" value={h.hero_title_line1} onChange={(v)=>setH("hero_title_line1",v)} testid="cms-hero-t1"/>
          <Text label="Título — linha 2" value={h.hero_title_line2} onChange={(v)=>setH("hero_title_line2",v)} testid="cms-hero-t2"/>
        </div>
        <Text label="Palavra em destaque (dentro da linha 1)" value={h.hero_highlight} onChange={(v)=>setH("hero_highlight",v)} testid="cms-hero-hi"/>
        <Text label="Descrição" value={h.hero_description} onChange={(v)=>setH("hero_description",v)} area rows={3} testid="cms-hero-desc"/>
        <div className="grid grid-cols-3 gap-3">
          <Text label="CTA primária" value={h.hero_cta_primary} onChange={(v)=>setH("hero_cta_primary",v)} testid="cms-hero-cta1"/>
          <Text label="CTA secundária" value={h.hero_cta_secondary} onChange={(v)=>setH("hero_cta_secondary",v)} testid="cms-hero-cta2"/>
          <Text label="CTA WhatsApp" value={h.hero_cta_whatsapp} onChange={(v)=>setH("hero_cta_whatsapp",v)} testid="cms-hero-ctawa"/>
        </div>
        <Text label="URL WhatsApp" value={h.hero_whatsapp_url} onChange={(v)=>setH("hero_whatsapp_url",v)} testid="cms-hero-wa-url"/>
        <div className="grid grid-cols-3 gap-3">
          <Text label="Imagem principal (URL)" value={h.hero_image_main} onChange={(v)=>setH("hero_image_main",v)} testid="cms-hero-img-main"/>
          <Text label="Imagem A (URL)" value={h.hero_image_a} onChange={(v)=>setH("hero_image_a",v)} testid="cms-hero-img-a"/>
          <Text label="Imagem B (URL)" value={h.hero_image_b} onChange={(v)=>setH("hero_image_b",v)} testid="cms-hero-img-b"/>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1"><Label className="text-slate-300 text-xs">Estatísticas (aparecem abaixo do hero)</Label>
            <Button size="sm" variant="outline" onClick={()=>addItem("stats",{value:"",label:""})} className="border-slate-700 h-7"><Plus size={12} className="mr-1"/>Adicionar</Button>
          </div>
          {(h.stats||[]).map((s,i)=>(
            <div key={i} className="grid grid-cols-[110px_1fr_auto] gap-2 mb-2 items-center" data-testid={`cms-stat-${i}`}>
              <Input value={s.value||""} onChange={(e)=>setListItem("stats",i,"value",e.target.value)} placeholder="15+" className="bg-[#07090E] border-slate-800 h-8"/>
              <Input value={s.label||""} onChange={(e)=>setListItem("stats",i,"label",e.target.value)} placeholder="peças mínimas" className="bg-[#07090E] border-slate-800 h-8"/>
              <Button size="sm" variant="outline" onClick={()=>rmItem("stats",i)} className="border-slate-700 h-8"><Trash2 size={12}/></Button>
            </div>
          ))}
        </div>
      </div>

      {/* CATEGORIAS */}
      <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 space-y-3">
        <div className="font-medium text-sm text-slate-300">Home — Categorias</div>
        <div className="grid grid-cols-2 gap-3">
          <Text label="Kicker" value={h.categories_kicker} onChange={(v)=>setH("categories_kicker",v)} testid="cms-cats-kicker"/>
          <Text label="Título" value={h.categories_title} onChange={(v)=>setH("categories_title",v)} testid="cms-cats-title"/>
        </div>
        <div className="flex items-center justify-between mb-1"><Label className="text-slate-300 text-xs">Cards de categoria</Label>
          <Button size="sm" variant="outline" onClick={()=>addItem("categories",{title:"",desc:"",image:""})} className="border-slate-700 h-7"><Plus size={12} className="mr-1"/>Adicionar</Button>
        </div>
        {(h.categories||[]).map((c,i)=>(
          <div key={i} className="rounded border border-slate-800 p-3 space-y-2" data-testid={`cms-cat-${i}`}>
            <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
              <Input value={c.title||""} onChange={(e)=>setListItem("categories",i,"title",e.target.value)} placeholder="Título" className="bg-[#07090E] border-slate-800 h-8"/>
              <Button size="sm" variant="outline" onClick={()=>rmItem("categories",i)} className="border-slate-700 h-8"><Trash2 size={12}/></Button>
            </div>
            <Textarea value={c.desc||""} onChange={(e)=>setListItem("categories",i,"desc",e.target.value)} placeholder="Descrição" rows={2} className="bg-[#07090E] border-slate-800"/>
            <Input value={c.image||""} onChange={(e)=>setListItem("categories",i,"image",e.target.value)} placeholder="URL imagem" className="bg-[#07090E] border-slate-800 h-8"/>
          </div>
        ))}
      </div>

      {/* TÉCNICAS / TECIDOS / INFO */}
      <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 space-y-3">
        <div className="font-medium text-sm text-slate-300">Home — Diferenciais / Técnicas / Tecidos / Info</div>
        <Text label="Kicker" value={h.info_kicker} onChange={(v)=>setH("info_kicker",v)} testid="cms-info-kicker"/>
        <div className="flex items-center justify-between mb-1"><Label className="text-slate-300 text-xs">Blocos</Label>
          <Button size="sm" variant="outline" onClick={()=>addItem("info_items",{title:"",desc:""})} className="border-slate-700 h-7"><Plus size={12} className="mr-1"/>Adicionar</Button>
        </div>
        {(h.info_items||[]).map((it,i)=>(
          <div key={i} className="rounded border border-slate-800 p-3 space-y-2" data-testid={`cms-info-${i}`}>
            <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
              <Input value={it.title||""} onChange={(e)=>setListItem("info_items",i,"title",e.target.value)} placeholder="Título" className="bg-[#07090E] border-slate-800 h-8"/>
              <Button size="sm" variant="outline" onClick={()=>rmItem("info_items",i)} className="border-slate-700 h-8"><Trash2 size={12}/></Button>
            </div>
            <Textarea value={it.desc||""} onChange={(e)=>setListItem("info_items",i,"desc",e.target.value)} placeholder="Descrição" rows={2} className="bg-[#07090E] border-slate-800"/>
          </div>
        ))}
      </div>

      {/* HOME CTA */}
      <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 space-y-3">
        <div className="font-medium text-sm text-slate-300">Home — Chamada final (CTA)</div>
        <Text label="Título" value={h.cta_title} onChange={(v)=>setH("cta_title",v)} testid="cms-cta-title"/>
        <Text label="Descrição" value={h.cta_description} onChange={(v)=>setH("cta_description",v)} area testid="cms-cta-desc"/>
        <Text label="Texto do botão" value={h.cta_button} onChange={(v)=>setH("cta_button",v)} testid="cms-cta-btn"/>
      </div>

      {/* PÁGINA PRODUTOS */}
      <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 space-y-3">
        <div className="font-medium text-sm text-slate-300">Página Produtos — Cabeçalho</div>
        <div className="grid grid-cols-2 gap-3">
          <Text label="Kicker" value={pp.kicker} onChange={(v)=>setP("kicker",v)} testid="cms-prodpg-kicker"/>
          <Text label="Título" value={pp.title} onChange={(v)=>setP("title",v)} testid="cms-prodpg-title"/>
        </div>
        <Text label="Descrição" value={pp.description} onChange={(v)=>setP("description",v)} area testid="cms-prodpg-desc"/>
      </div>

      {/* CONTATO */}
      <div className="rounded-lg border border-slate-800 bg-[#0B0F17] p-4 space-y-3">
        <div className="font-medium text-sm text-slate-300">Página Contato</div>
        <div className="grid grid-cols-2 gap-3">
          <Text label="Kicker" value={cc.kicker} onChange={(v)=>setC("kicker",v)} testid="cms-ct-kicker"/>
          <Text label="Título" value={cc.title} onChange={(v)=>setC("title",v)} testid="cms-ct-title"/>
        </div>
        <Text label="Descrição" value={cc.description} onChange={(v)=>setC("description",v)} area testid="cms-ct-desc"/>
        <div className="grid grid-cols-3 gap-3">
          <Text label="URL WhatsApp" value={cc.whatsapp_url} onChange={(v)=>setC("whatsapp_url",v)} testid="cms-ct-wa"/>
          <Text label="E-mail" value={cc.email} onChange={(v)=>setC("email",v)} testid="cms-ct-email"/>
          <Text label="Atendimento (região)" value={cc.location} onChange={(v)=>setC("location",v)} testid="cms-ct-loc"/>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="w-full bg-[#0CDBF2] hover:bg-[#0ABED4] text-[#07090E] font-semibold" data-testid="cms-save">
        {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Save size={14} className="mr-2"/>}Salvar conteúdo do site
      </Button>
    </div>
  );
}
